import React, { useCallback } from "react";
import { Icon } from "@iconify/react";
import saveFill from "@iconify/icons-eva/save-outline";
import {
  validateRut,
  deconstructRut,
  formatRut,
  RutFormat,
} from "@fdograph/rut-utilities";
import {
  Dialog,
  TextField,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Autocomplete,
} from "@material-ui/core";
import { LoadingButton } from "@material-ui/lab";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import { sucursalesApi } from "src/services/sucursalesApi";
import { ajustesApi } from "src/services/ajustesApi";
import { useSnackbar } from "src/components/snackbar";

export default function FormDialog(props) {
  const {
    open,
    setOpen,
    nombre,
    label,
    direccion,
    comuna,
    telefono,
    gerente,
    razonSocial,
    rutRazonSocial,
    representanteLegal,
    rutRepresentante,
    editar,
    apiOpenFacturaConfigurada,
    apiWebpayConfigurada,
    webpayCommerceCode,
    rutEmisorOF,
  } = props;

  const [agregarSucursal, { isLoading: guardandoDatos }] =
    sucursalesApi.useAddSucursalMutation();
  const [editarSucursal, { isLoading: editandoDatos }] =
    sucursalesApi.useUpdateSucursalMutation();
  const { data: comunas = [] } = ajustesApi.useGetComunasQuery();
  const { enqueueSnackbar } = useSnackbar();

  /**
   * Se encarga de cerrar el modal de agregar sucursal
   */
  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  /**
   * Se encarga de hacer las verificaciones para los distinso campos del
   * formulario de agregar sucursales.
   */
  const LoginSchema = Yup.object().shape({
    nombre: Yup.string().required("El ID de la sucursal es requerido"),
    label: Yup.string()
      .required("El nombre de la sucursal es requerido")
      .max(20, "El nombre no puede tener más de 20 caracteres"),
    direccion: Yup.string().required("Dirección requerida"),
    comuna: Yup.string().required("Comuna requerida").nullable(),
    telefono: Yup.string().required("Teléfono requerido"),
    gerente: Yup.string().required("Nombre del gerente requerido"),
    razonSocial: Yup.string().required("Razón social requerida"),
    rutRazonSocial: Yup.string().test("rut test", "Rut no válido", (value) =>
      validateRut(value),
    ),
    representanteLegal: Yup.string().required("Nombre Requerido"),
    rutRepresentante: Yup.string().test("rut test", "Rut no válido", (value) =>
      validateRut(value),
    ),
  });

  /**
   * Objeto encargado de validar los valores ingresados al formulario, según las
   * validaciones entregadas por LoginSchema.
   */
  const formik = useFormik({
    initialValues: {
      nombre: nombre,
      label: label,
      direccion: direccion,
      comuna: comuna,
      telefono: telefono,
      gerente: gerente,
      razonSocial: razonSocial,
      rutRazonSocial: rutRazonSocial,
      representanteLegal: representanteLegal,
      rutRepresentante: rutRepresentante,
      apiOpenFactura: "",
      commerceCodeWebpay: "",
      apiKeyWebpay: "",
      remember: true,
    },
    validationSchema: LoginSchema,
    // Se retorna la promesa para que el formulario la espere y devuelva el boton a su estado
    // normal cuando termine. Sin retornarla, el envio queda marcado como en curso para
    // siempre: al guardar bien no se nota porque el modal se cierra, pero ante un error el
    // modal queda abierto con el boton girando y sin forma de reintentar. `guardarDatos`
    // atrapa sus propios errores, asi que siempre resuelve.
    onSubmit: () => guardarDatos(),
  });

  const {
    errors,
    touched,
    handleSubmit,
    getFieldProps,
    setFieldValue,
    isSubmitting,
  } = formik;

  /**
   * Se encargar de guardar los datos ingresados por el usuario, ya validados
   * a la variable datos, que es la que se guardará en la bd.
   */
  const guardarDatos = useCallback(async () => {
    try {
      const sucursal = {
        nombre: getFieldProps("nombre").value.toUpperCase(),
        label: getFieldProps("label").value.toUpperCase(),
        direccion: getFieldProps("direccion").value.toUpperCase(),
        comuna: getFieldProps("comuna").value.toUpperCase(),
        telefono: getFieldProps("telefono").value.toString().toUpperCase(),
        gerente: getFieldProps("gerente").value.toUpperCase(),
        razonSocial: getFieldProps("razonSocial").value.toUpperCase(),
        rutRazonSocial:
          deconstructRut(getFieldProps("rutRazonSocial").value.toUpperCase())
            .digits +
          deconstructRut(getFieldProps("rutRazonSocial").value.toUpperCase())
            .verifier,
        representanteLegal:
          getFieldProps("representanteLegal").value.toUpperCase(),
        rutRepresentante:
          deconstructRut(getFieldProps("rutRepresentante").value.toUpperCase())
            .digits +
          deconstructRut(getFieldProps("rutRepresentante").value.toUpperCase())
            .verifier,
      };

      // La API key OpenFactura es write-only: solo se envía si el admin la
      // ingresó. El backend la cifra y valida contra /organization; nunca se
      // aplica toUpperCase ni se precarga en el formulario.
      const apiKeyIngresada = getFieldProps("apiOpenFactura").value?.trim();
      if (apiKeyIngresada) {
        sucursal.apiOpenFactura = apiKeyIngresada;
      }

      // La credencial Webpay es write-only y consta de dos partes: el código de
      // comercio (no secreto, se muestra el actual) y la API Key (secreta, nunca
      // se precarga). Regla: ambos vacíos conserva la credencial actual; ambos
      // informados la reemplaza; solo uno informado es error (no se envía a medias).
      const commerceCodeWebpay =
        getFieldProps("commerceCodeWebpay").value?.trim();
      const apiKeyWebpay = getFieldProps("apiKeyWebpay").value?.trim();
      if (Boolean(commerceCodeWebpay) !== Boolean(apiKeyWebpay)) {
        enqueueSnackbar(
          "Para configurar Webpay debes ingresar el código de comercio y la API Key. Completa ambos o deja ambos vacíos.",
          {
            variant: "warning",
            anchorOrigin: { vertical: "bottom", horizontal: "right" },
          },
        );
        return;
      }
      if (commerceCodeWebpay && apiKeyWebpay) {
        sucursal.apiWebpay = JSON.stringify({
          commerceCode: commerceCodeWebpay,
          apiKey: apiKeyWebpay,
        });
      }

      let respuesta;
      if (editar) {
        respuesta = await editarSucursal({
          id: getFieldProps("nombre").value.toUpperCase(),
          body: {
            ...sucursal,
          },
        }).unwrap();
      } else {
        respuesta = await agregarSucursal({
          ...sucursal,
          nombre: getFieldProps("nombre").value.toUpperCase(),
        }).unwrap();
      }

      // `warning` solo llega cuando se configuró una credencial de OpenFactura
      // nueva y el contribuyente al que pertenece no es la sociedad de la
      // oficina: la sucursal quedó guardada y emitirá con esa cuenta. No es un
      // error -> se suma al aviso del guardado en vez de salir en uno aparte que
      // contradiga al que el usuario acaba de ver. Lo que sí impide guardar
      // corta la petición y llega por el catch.
      const guardado = editar
        ? "Sucursal editada correctamente"
        : "Sucursal añadida correctamente";
      const notaCuentaEmisora = respuesta?.warning;

      const opcionesAviso = {
        variant: notaCuentaEmisora ? "info" : "success",
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "right",
        },
      };
      if (notaCuentaEmisora) {
        // La nota nombra dos RUT y no alcanza a leerse en lo que dura un aviso
        // corriente.
        opcionesAviso.autoHideDuration = 10000;
      }

      enqueueSnackbar(
        notaCuentaEmisora ? `${guardado}. ${notaCuentaEmisora}` : guardado,
        opcionesAviso,
      );

      handleClose();
    } catch (error) {
      const texto = `Error al ${editar ? "editar" : "guardar"} la sucursal`;
      console.error(`${texto}: `, error);
      enqueueSnackbar(error?.data?.message || error?.message || `${texto}`, {
        variant: "error",
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "right",
        },
      });
    }
  }, [
    agregarSucursal,
    editar,
    editarSucursal,
    enqueueSnackbar,
    getFieldProps,
    handleClose,
  ]);

  // Contribuyente con el que la oficina emite ante OpenFactura. Lo determina la
  // credencial guardada y no el formulario, así que se muestra en solo lectura:
  // es lo que permite contrastar con qué cuenta emite la oficina antes de
  // reemplazar la credencial, y a qué cuenta se refiere el aviso del guardado.
  //
  // Con credencial y sin cuenta registrada lo dice tal cual, porque ese estado
  // existe: dar la cuenta por no configurada contradiría al propio campo, cuyo
  // rótulo anuncia que la credencial sí lo está. Sin credencial no dice nada,
  // igual que el campo de Webpay ante el mismo estado.
  let cuentaEmisoraActual = "";
  if (rutEmisorOF) {
    cuentaEmisoraActual = `Cuenta emisora actual: ${formatRut(
      rutEmisorOF,
      RutFormat.DOTS_DASH,
    )}`;
  } else if (apiOpenFacturaConfigurada) {
    cuentaEmisoraActual =
      "Credencial configurada, sin cuenta emisora registrada";
  }

  return (
    <>
      <div>
        <Dialog
          open={open}
          aria-labelledby="form-dialog-title"
          id="dialogSucursal"
        >
          <DialogTitle id="form-dialog-title">Sucursal</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Complete la información de la sucursal
            </DialogContentText>

            <FormikProvider value={formik}>
              <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                <TextField
                  required
                  autoFocus
                  margin="dense"
                  id="nombre"
                  label="ID Sucursal"
                  fullWidth
                  disabled={editar}
                  {...getFieldProps("nombre")}
                  error={Boolean(touched.nombre && errors.nombre)}
                  helperText={touched.nombre && errors.nombre}
                />
                <TextField
                  required
                  margin="dense"
                  id="label"
                  label="Nombre sucursal"
                  fullWidth
                  {...getFieldProps("label")}
                  error={Boolean(touched.label && errors.label)}
                  helperText={touched.label && errors.label}
                />

                <TextField
                  required
                  margin="dense"
                  id="direccion"
                  label="Dirección"
                  fullWidth
                  {...getFieldProps("direccion")}
                  error={Boolean(touched.direccion && errors.direccion)}
                  helperText={touched.direccion && errors.direccion}
                />

                <Autocomplete
                  style={{ marginTop: "10px" }}
                  {...getFieldProps("comuna")}
                  disablePortal
                  options={comunas}
                  isOptionEqualToValue={(option, value) => {
                    return option.nombre === value;
                  }}
                  getOptionLabel={(option) =>
                    option.nombre ? option.nombre : option
                  }
                  onChange={(event, newValue) => {
                    if (newValue !== null) {
                      setFieldValue("comuna", newValue.nombre, true);
                    }
                  }}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={Boolean(touched.comuna && errors.comuna)}
                      fullWidth
                      helperText={touched.comuna && errors.comuna}
                      label="Seleccione Comuna"
                      name="comuna"
                      variant="outlined"
                    />
                  )}
                />

                <TextField
                  required
                  margin="dense"
                  id="telefono"
                  label="Teléfono"
                  type="number"
                  fullWidth
                  {...getFieldProps("telefono")}
                  error={Boolean(touched.telefono && errors.telefono)}
                  helperText={touched.telefono && errors.telefono}
                />
                <TextField
                  required
                  margin="dense"
                  id="gerente"
                  label="Nombre del Gerente"
                  fullWidth
                  {...getFieldProps("gerente")}
                  error={Boolean(touched.gerente && errors.gerente)}
                  helperText={touched.gerente && errors.gerente}
                />
                <TextField
                  required
                  margin="dense"
                  id="razonSocial"
                  label="Razón Social de la Sucursal"
                  fullWidth
                  {...getFieldProps("razonSocial")}
                  error={Boolean(touched.razonSocial && errors.razonSocial)}
                  helperText={touched.razonSocial && errors.razonSocial}
                />
                <TextField
                  required
                  margin="dense"
                  id="rutRazonSocial"
                  label="Rut de la Razón Social"
                  fullWidth
                  {...getFieldProps("rutRazonSocial")}
                  error={Boolean(
                    touched.rutRazonSocial && errors.rutRazonSocial,
                  )}
                  helperText={touched.rutRazonSocial && errors.rutRazonSocial}
                />
                <TextField
                  required
                  margin="dense"
                  id="representanteLegal"
                  label="Nombre del Representante Legal de la Sucursal"
                  fullWidth
                  {...getFieldProps("representanteLegal")}
                  error={Boolean(
                    touched.representanteLegal && errors.representanteLegal,
                  )}
                  helperText={
                    touched.representanteLegal && errors.representanteLegal
                  }
                />
                <TextField
                  required
                  margin="dense"
                  id="rutRepresentanteLegal"
                  label="Rut del Representante legal de la Sucursal"
                  fullWidth
                  {...getFieldProps("rutRepresentante")}
                  error={Boolean(
                    touched.rutRepresentante && errors.rutRepresentante,
                  )}
                  helperText={
                    touched.rutRepresentante && errors.rutRepresentante
                  }
                />
                <TextField
                  margin="dense"
                  id="apiOpenFactura"
                  label={
                    editar && apiOpenFacturaConfigurada
                      ? "API Key OpenFactura (configurada — ingresar para reemplazar)"
                      : "API Key OpenFactura"
                  }
                  type="password"
                  autoComplete="new-password"
                  fullWidth
                  {...getFieldProps("apiOpenFactura")}
                  error={Boolean(
                    touched.apiOpenFactura && errors.apiOpenFactura,
                  )}
                  helperText={
                    (touched.apiOpenFactura && errors.apiOpenFactura) ||
                    (editar ? cuentaEmisoraActual : "")
                  }
                />

                <TextField
                  margin="dense"
                  id="commerceCodeWebpay"
                  label={
                    editar && apiWebpayConfigurada
                      ? "Código de comercio Webpay (configurado — ingresar para reemplazar)"
                      : "Código de comercio Webpay"
                  }
                  fullWidth
                  {...getFieldProps("commerceCodeWebpay")}
                  helperText={
                    editar && webpayCommerceCode
                      ? `Comercio actual: ${webpayCommerceCode}`
                      : ""
                  }
                />
                <TextField
                  margin="dense"
                  id="apiKeyWebpay"
                  label="API Key Webpay"
                  type="password"
                  autoComplete="new-password"
                  fullWidth
                  {...getFieldProps("apiKeyWebpay")}
                />

                <DialogActions>
                  <LoadingButton onClick={handleClose} color="error">
                    Cancelar
                  </LoadingButton>
                  <LoadingButton
                    type="submit"
                    startIcon={<Icon icon={saveFill} />}
                    color="primary"
                    loading={isSubmitting || guardandoDatos || editandoDatos}
                  >
                    Guardar Sucursal
                  </LoadingButton>
                </DialogActions>
              </Form>
            </FormikProvider>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
